import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { lccApi } from '@/lib/lcc/api';
import { StatusLamp } from '@/components/pnr/airport-ui';
import { toneClasses, type BoardTone } from '@/components/pnr/airport-tokens';

/** Read the replacement purchase journal without projecting it onto the legacy FSM. */
export function LccStatusBar({ caseUrn, groupId }: { caseUrn: string; groupId: string }) {
  const { t } = useTranslation('lcc');
  const purchases = useQuery({
    queryKey: ['lcc', 'purchases', caseUrn, groupId],
    queryFn: () => lccApi.purchases(caseUrn, groupId),
    retry: false,
    staleTime: 0,
  });
  // The workspace sets this immediately before submitting. Transport failure
  // may leave no receipt to render, but must never look like a fresh purchase.
  const attempted = useQuery<boolean>({
    queryKey: ['lcc', 'attempted', caseUrn, groupId],
    queryFn: async () => false,
    enabled: false,
    initialData: false,
  });
  const receipt =
    purchases.data?.purchases.find((p) => p.status === 'CONFIRMED') ?? purchases.data?.purchases[0];
  let kind = 'external-supervised';
  let tone: BoardTone = 'info';
  let title = t('supervisedTitle');
  let body = t('supervisedNext');
  if (receipt) {
    kind = `external-${receipt.status.toLowerCase()}`;
    tone = receipt.status === 'CONFIRMED' ? 'ok' : receipt.status === 'FAILED' ? 'alert' : 'warn';
    title =
      receipt.status === 'CONFIRMED'
        ? t(receipt.mode === 'sandbox' ? 'receiptTestConfirmed' : 'receiptConfirmed')
        : t('receiptStatus', { status: t(`status.${receipt.status}`) });
    body = [
      receipt.provider === 'duffel' ? 'Duffel' : 'Atlas',
      t(`mode.${receipt.mode}`),
      receipt.bookingReference
        ? t('receiptReference', { reference: receipt.bookingReference })
        : undefined,
      receipt.status === 'CONFIRMED' && receipt.mode === 'sandbox'
        ? t('receiptNoTravel')
        : undefined,
      receipt.status !== 'CONFIRMED' ? t('receiptCheckExisting') : undefined,
    ]
      .filter(Boolean)
      .join(' · ');
  } else if (attempted.data) {
    kind = 'external-unknown';
    tone = 'warn';
    title = t('receiptStatus', { status: t('status.UNKNOWN') });
    body = t('receiptCheckExisting');
  } else if (purchases.isError) {
    kind = 'external-unavailable';
    tone = 'warn';
    body = t('receiptUnavailable');
  } else if (purchases.isPending) {
    kind = 'external-loading';
    body = t('receiptLoading');
  }
  const styles = toneClasses(tone);
  return (
    <div
      className={cn(
        'flex min-h-12 shrink-0 flex-wrap items-center gap-x-2 gap-y-0.5 border-b py-1 pl-5 pr-24 text-sm sm:pl-6',
        styles.surface,
        styles.border,
      )}
      data-testid="pnr-resolution-bar"
      data-kind={kind}
      role="status"
      aria-live="polite"
    >
      <StatusLamp tone={tone} />
      <span className="font-semibold">{title}</span>
      <span className="text-muted-foreground min-w-0 break-words">{body}</span>
    </div>
  );
}
